import { AbstractControl, FormArray, FormGroup } from "@angular/forms";
import {
  DEVICE_ATTRIBUTE_COLUMN_PREFIX,
  ProductAttributeScope,
  ProductAttributeType,
  ProductCategory,
} from "../../master-data/master-data.model";
import { ExpressionColumnInfo } from "../table-field/expression-validator";
import { TableFieldType } from "../table-field/table-field.model";
import { ControlType } from "./item.model";

/**
 * The field form groups of a template form: top-level fields and the fields of
 * every section.
 */
export function templateFieldForms(template: FormGroup | null | undefined): FormGroup[] {
  if (!template) {
    return [];
  }
  const fields = (template.get("fields") as FormArray | null)?.controls ?? [];
  const sections = (template.get("groups") as FormArray | null)?.controls ?? [];
  return [
    ...fields,
    ...sections.flatMap(
      (section) => (section.get("fields") as FormArray | null)?.controls ?? [],
    ),
  ] as FormGroup[];
}

/** Whether the template is filled in as an attachment (and so possibly per device). */
export function isAttachmentOnly(template: FormGroup | null | undefined): boolean {
  return !!template?.get("attachmentOnly")?.value;
}

/**
 * How much of an option field's list has a numeric value: `hasAny` makes it usable
 * in a formula, `allMapped` says no option leaves the formula blank.
 */
export function numericCoverage(field: AbstractControl): {
  hasAny: boolean;
  allMapped: boolean;
} {
  const options = ((field.get("values") as FormArray | null)?.controls ?? [])
    .map((c) => (c.value as string | null) ?? "")
    .filter((v) => v !== "");
  const map = parseValueMap(field.get("valueMap")?.value as string | null);
  if (options.length === 0) {
    return { hasAny: false, allMapped: false };
  }
  const mapped = options.filter((opt) => map[opt] != null && !isNaN(+map[opt])).length;
  return { hasAny: mapped > 0, allMapped: mapped === options.length };
}

export function parseValueMap(raw: string | null | undefined): Record<string, number | null> {
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * The variables an EQUATION or CONDITION field of the template can use, as the
 * mobile app binds them (DocumentFormulaScope): the template's fields by render tag
 * (only INTEGER, DECIMAL and RADIO/DROPDOWN with numbers evaluate; the others are
 * listed so the validator can say why they can't be used, including other formula
 * fields), and, in an attachment-only template, the numeric fixed attributes of all
 * product categories by key. A field with the same name wins.
 *
 * @param exceptRenderTag the field being edited
 */
export function formulaVariables(
  template: FormGroup | null | undefined,
  productCategories: ProductCategory[] | null | undefined,
  exceptRenderTag?: string | null,
): ExpressionColumnInfo[] {
  const variables: ExpressionColumnInfo[] = [];
  const names = new Set<string>();
  for (const field of templateFieldForms(template)) {
    const key = field.get("renderTag")?.value as string | null;
    if (!key || key === exceptRenderTag || names.has(key)) {
      continue;
    }
    names.add(key);
    const coverage = numericCoverage(field);
    variables.push({
      key,
      type: +field.get("type")?.value as TableFieldType,
      hasNumericValueMap: coverage.hasAny,
      allValuesNumeric: coverage.allMapped,
    });
  }
  if (isAttachmentOnly(template)) {
    for (const key of fixedAttributeKeys(productCategories)) {
      if (!names.has(key)) {
        names.add(key);
        variables.push({ key, type: TableFieldType.DECIMAL });
      }
    }
  }
  return variables;
}

/** Keys of the numeric fixed (PRODUCT-scope) attributes of the categories. */
export function fixedAttributeKeys(productCategories: ProductCategory[] | null | undefined): string[] {
  const keys = new Set<string>();
  for (const category of productCategories ?? []) {
    for (const attribute of category.productCategoryAttributes ?? []) {
      const numeric =
        +attribute.productAttributeType === ProductAttributeType.INTEGER ||
        +attribute.productAttributeType === ProductAttributeType.DECIMAL;
      const fixed = (attribute.scope ?? ProductAttributeScope.PRODUCT) === ProductAttributeScope.PRODUCT;
      if (numeric && fixed && attribute.key) {
        keys.add(attribute.key);
      }
    }
  }
  return [...keys];
}

/** The attribute key of a device attribute field (`devAttr_<key>`), else null. */
export function deviceAttributeKey(renderTag: string | null | undefined): string | null {
  return renderTag?.startsWith(DEVICE_ATTRIBUTE_COLUMN_PREFIX)
    ? renderTag.substring(DEVICE_ATTRIBUTE_COLUMN_PREFIX.length)
    : null;
}

/**
 * The device attribute keys of an attachment template that its category doesn't
 * have at all: those fields stay empty on the form. Both scopes count: an
 * editable attribute is filled from the device, a fixed one from the product
 * (shown read-only on mobile). An unknown category reports nothing.
 */
export function attributeKeysMissingFromCategory(
  keys: string[],
  category: ProductCategory | null | undefined,
): string[] {
  if (!category) {
    return [];
  }
  const known = new Set(
    (category.productCategoryAttributes ?? []).map((attribute) => attribute.key),
  );
  return keys.filter((key) => !known.has(key));
}

/** The field type for a device attribute; timestamps are entered as text, like on products. */
export function deviceAttributeFieldType(type: ProductAttributeType): ControlType {
  switch (+type) {
    case ProductAttributeType.BOOLEAN:
      return ControlType.BOOLEAN;
    case ProductAttributeType.INTEGER:
      return ControlType.INTEGER;
    case ProductAttributeType.DECIMAL:
      return ControlType.DECIMAL;
    default:
      return ControlType.TEXT;
  }
}
